/**
 * The body of the `/about-us/leadership-team` page: the dark hero (`./LeadershipHeader.tsx`)
 * above the portrait grid (`./LeadershipSection.tsx`).
 *
 * Position: rendered only by `src/pages/about-us/leadership-team/index.tsx`, which passes the hero
 * copy from `./LeadershipTeamContent.tsx` and the leader list from
 * `src/contentful/leader/getContentfulLeaders.ts`. Not to be confused with
 * `components/AboutUs/LeadershipTeam/LeadershipTeam.tsx`, the shorter section on `/about-us`.
 */
import React from "react";
import LeadershipHeader from "./LeadershipHeader";
import LeadershipSection from "./LeadershipSection";
import { ProcessedContentfulLeadersFieldsByLanguage } from "@contentful/leader/types";

/**
 * The hero copy of the leadership team page, per language in `./LeadershipTeamContent.tsx`.
 * `className` and `isMobile` are not read by `./LeadershipHeader.tsx`.
 */
export interface LeadershipTeamContent {
	className?: string;
	/** The page heading. */
	title: string;
	/** Lead paragraph: the larger text on desktop, an `h4` on mobile. */
	context1?: string;
	/** Second paragraph, beside the lead on desktop and below it on mobile. */
	context2?: string;
	isMobile?: boolean;
}

/** Props of the leadership team page body. */
export type LeadershipTeamProps = {
	/** The current language's hero copy. */
	content: LeadershipTeamContent;
	/** Leader cards in every language; `Team/ContentfulLeaders.tsx` picks the current one. */
	contentfulLeadershipTeam: ProcessedContentfulLeadersFieldsByLanguage;
};

/** Renders the leadership team page's hero and portrait grid. */
const LeadershipTeam: React.FC<LeadershipTeamProps> = ({ content, contentfulLeadershipTeam }) => {
	return (
		<>
			<LeadershipHeader content={content} />
			<LeadershipSection contentfulLeadershipTeam={contentfulLeadershipTeam} />
		</>
	);
};

export default LeadershipTeam;
